import clsx from "clsx";
import { useEffect, useState } from "react";

import { useDriverLocations, useWorkspaceConnections } from "@web-app/api";
import {
  AccordionV2,
  Avatar,
  Icon,
  OrderCard,
  RouteCard,
  Skeleton,
} from "@web-app/components";
import { focusOnOrder } from "@web-app/pages/common/DispatchMap/mapFocusUtils";
import useDispatchStore from "@web-app/store/dispatchStore";
import { BaseApp, Order, Route, User } from "@web-app/types";
import { pluralize } from "@web-app/utils/helpers";

import { getContentCountTexts } from "../../utils";
import EmptyState from "../EmptyState";

import useConnectionsDriversView, {
  type WorkspaceAssignee,
} from "./useConnectionsDriversView";

interface ConnectionsDriversViewProps {
  appsMap: Map<string, BaseApp>;
  assigneesMap: Map<string, User>;
  autoAssignSearchingIds: Set<string>;
  isAppsLoading?: boolean;
  isAssigneesLoading?: boolean;
  isLoadingOrders: boolean;
  onSelectExternalProvider: (
    entity: Order | Route | null,
    provider: string,
    type: string,
  ) => void;
  onShare: (orderId: string, connectedWorkspaceId: string) => void;
  routedOrdersMap: Map<string, Order>;
  routes: Route[];
  searchTerm: string;
  unroutedOrders: Order[];
}

export default function ConnectionsDriversView({
  appsMap,
  assigneesMap,
  autoAssignSearchingIds,
  isAppsLoading,
  isAssigneesLoading,
  unroutedOrders,
  routedOrdersMap,
  onShare,
  searchTerm,
  routes,
  isLoadingOrders,
  onSelectExternalProvider,
}: ConnectionsDriversViewProps) {
  const {
    isOnlineDriversLoading,
    ordersGroupedByWorkspace,
    unassignedOnlineDrivers,
  } = useConnectionsDriversView({
    assigneesMap,
    routes,
    searchTerm,
    unroutedOrders,
  });

  const currentUser = useDispatchStore((state) => state.user);
  const [listState, setListState] = useState<{
    [key: string]: {
      assigneeList: {
        [key: string]: {
          isOpen: boolean;
        };
      };
      isOpen: boolean;
    };
  }>({});
  useEffect(() => {
    ordersGroupedByWorkspace.forEach((workspace) => {
      const workspaceStateId = `workspace-card-${workspace.id}`;
      setListState((prev) => {
        const workspaceState = localStorage.getItem(workspaceStateId);

        if (workspaceState) {
          return {
            ...prev,
            [workspaceStateId]: JSON.parse(workspaceState),
          };
        }

        return {
          ...prev,
          [workspaceStateId]: {
            assigneeList: {},
            isOpen: false,
          },
        };
      });
    });
  }, [ordersGroupedByWorkspace]);

  const { data: workspaceConnections } = useWorkspaceConnections();

  const { data } = useDriverLocations();
  const driverLocations = data?.driverLocations;

  const focusedAssigneeId = useDispatchStore(
    (state) => state.focusedAssigneeId,
  );

  const isLocationPinVisible = (assignee: WorkspaceAssignee) => {
    return !!driverLocations?.[assignee.driverProfileId ?? ""];
  };

  if (isLoadingOrders) {
    return (
      <div className="flex flex-col">
        <div className="flex flex-col gap-2 mt-2 px-4">
          <Skeleton className="" height="h-[40px] lg:h-[61px]" width="w-full" />
          <Skeleton className="" height="h-[40px] lg:h-[61px]" width="w-full" />
          <Skeleton className="" height="h-[40px] lg:h-[61px]" width="w-full" />
          <Skeleton
            height="h-[40px] lg:h-[61px]"
            width="w-full"
            className=" bg-gradient-to-b from-gray-200 !bg-white to-gray-50"
          />
        </div>
      </div>
    );
  }

  const hasTasks = unroutedOrders.length > 0 || routes.length > 0;

  if (!hasTasks && isOnlineDriversLoading) {
    return (
      <div className="flex flex-col">
        <div className="flex flex-col gap-2 mt-2 px-4">
          <Skeleton className="" height="h-[40px] lg:h-[61px]" width="w-full" />
          <Skeleton className="" height="h-[40px] lg:h-[61px]" width="w-full" />
        </div>
      </div>
    );
  }

  if (!hasTasks && !isOnlineDriversLoading && !unassignedOnlineDrivers.length) {
    return <EmptyState />;
  }

  return (
    <div className="flex flex-col relative z-10 pb-20 overflow-y-auto h-full">
      {ordersGroupedByWorkspace.map((workspace) => {
        const stateId = `workspace-card-${workspace.id}`;
        const workspaceState = listState[stateId];
        const isOpen = workspaceState?.isOpen ?? false;

        return (
          <AccordionV2
            key={workspace.id}
            isBorderless
            defaultOpen={isOpen}
            onLabelClick={(open) => {
              const updatedWorkspaceCardState = {
                ...listState[stateId],
                isOpen: open,
              };

              localStorage.setItem(
                stateId,
                JSON.stringify(updatedWorkspaceCardState),
              );
              setListState((prev) => {
                return {
                  ...prev,
                  [stateId]: updatedWorkspaceCardState,
                };
              });
            }}
          >
            <AccordionV2.Label>
              <div className="flex flex-col items-start pl-4">
                <span className="text-gray-700 font-normal text-xs lg:text-sm flex gap-2 items-center">
                  <Avatar image={workspace.avatarUrl ?? undefined} size={8} />
                  <span className="flex flex-col items-start">
                    {workspace.name}
                    <span className="text-gray-500 font-normal text-[10px] lg:text-xs">
                      {workspace.assignees.size}{" "}
                      {pluralize("driver", workspace.assignees.size)}
                    </span>
                  </span>
                </span>
              </div>
            </AccordionV2.Label>
            <AccordionV2.Content>
              <div className="flex flex-col bg-gray-100">
                {Array.from(workspace.assignees.values()).map((assignee) => {
                  const contentCountText = getContentCountTexts(
                    assignee.routes,
                    assignee.orders,
                  );

                  return (
                    <AccordionV2
                      key={assignee.id}
                      isBorderless
                      defaultOpen={
                        workspaceState?.assigneeList[assignee.id]?.isOpen ??
                        false
                      }
                      onLabelClick={(open) => {
                        const updatedAssigneeCardState = {
                          ...workspaceState?.assigneeList,
                          [assignee.id]: {
                            isOpen: open,
                          },
                        };

                        setListState((prev) => {
                          return {
                            ...prev,
                            [stateId]: {
                              ...prev[stateId],
                              assigneeList: updatedAssigneeCardState,
                            },
                          };
                        });
                        localStorage.setItem(
                          stateId,
                          JSON.stringify({
                            ...listState[stateId],
                            assigneeList: updatedAssigneeCardState,
                          }),
                        );
                      }}
                    >
                      <AccordionV2.Label>
                        <div className="flex flex-col items-start pl-4">
                          <span className="text-gray-700 font-normal text-xs lg:text-sm flex gap-2 items-center">
                            <div className="">
                              <Avatar
                                image={assignee.avatarUrl ?? undefined}
                                size={8}
                                dutyStatus={assignee.dutyStatus}
                              />
                            </div>
                            <span className="flex flex-col items-start">
                              <span className="flex gap-2 items-center">
                                <span className="flex items-center gap-1">
                                  {isAssigneesLoading &&
                                    assignee.name === "Loading..." && (
                                      <Icon
                                        name="spinner"
                                        className="w-3 h-3 animate-spin"
                                      />
                                    )}
                                  {assignee.name}
                                </span>
                                {isLocationPinVisible(assignee) && (
                                  <Icon
                                    name="locationCrossHairs"
                                    className={clsx(
                                      "w-3 cursor-pointer p-1 rounded-full hover:bg-gray-200",
                                      focusedAssigneeId ===
                                        assignee.driverProfileId
                                        ? "bg-gray-200"
                                        : "",
                                    )}
                                    onClick={() => {
                                      useDispatchStore.setState({
                                        focusedAssigneeId:
                                          focusedAssigneeId !==
                                          assignee.driverProfileId
                                            ? assignee.driverProfileId
                                            : null,
                                      });
                                    }}
                                  />
                                )}
                              </span>
                              <span className="text-gray-500 font-normal text-[10px] lg:text-xs">
                                {contentCountText}
                              </span>
                            </span>
                          </span>
                        </div>
                      </AccordionV2.Label>
                      <AccordionV2.Content>
                        <div className="flex flex-col gap-2 mb-2 px-4 py-2">
                          {assignee.routes.map((route) => (
                            <RouteCard
                              key={route.id}
                              route={route}
                              routedOrdersMap={routedOrdersMap}
                              searchTerm={searchTerm}
                              className="bg-white"
                              onSelectExternalProvider={
                                onSelectExternalProvider
                              }
                              assigneesMap={assigneesMap}
                              isAssigneesLoading={isAssigneesLoading}
                              isAutoAssignSearching={autoAssignSearchingIds.has(
                                route.id,
                              )}
                              appsMap={appsMap}
                              isAppsLoading={isAppsLoading}
                            />
                          ))}
                          {assignee.orders.map((order) => (
                            <OrderCard
                              key={order.id}
                              order={order}
                              app={
                                order.appId ? appsMap.get(order.appId) : null
                              }
                              isAppsLoading={isAppsLoading}
                              assignee={
                                order.assigneeId
                                  ? assigneesMap.get(order.assigneeId)
                                  : null
                              }
                              isAssigneesLoading={isAssigneesLoading}
                              isAutoAssignSearching={autoAssignSearchingIds.has(
                                order.id,
                              )}
                              searchTerm={searchTerm}
                              workspaceConnections={workspaceConnections || []}
                              onViewDetails={() => {
                                useDispatchStore.setState({
                                  focusedOrderId: order.id,
                                  mapFocusedOrderId: order.id,
                                });

                                setTimeout(() => {
                                  const { dispatchMapRef } =
                                    useDispatchStore.getState();
                                  if (dispatchMapRef?.current) {
                                    focusOnOrder({
                                      mapRef: dispatchMapRef.current,
                                      order,
                                    });
                                  }
                                }, 0);
                              }}
                              onShare={
                                currentUser?.currentWorkspaceId ===
                                order?.workspaceId
                                  ? (connectedWorkspaceId) =>
                                      onShare(order.id, connectedWorkspaceId)
                                  : undefined
                              }
                              onSelectExternalProvider={
                                onSelectExternalProvider
                              }
                              className="bg-white"
                            />
                          ))}
                        </div>
                      </AccordionV2.Content>
                    </AccordionV2>
                  );
                })}
              </div>
            </AccordionV2.Content>
          </AccordionV2>
        );
      })}
      {unassignedOnlineDrivers.length > 0 && (
        <AccordionV2 isBorderless defaultOpen={false}>
          <AccordionV2.Label>
            <div className="flex flex-col items-start px-4 py-2">
              <span className="text-gray-700 font-normal !text-xs lg:!text-sm">
                Unassigned Online Drivers
              </span>
              <span className="text-gray-500 font-normal text-[10px] lg:text-xs">
                {unassignedOnlineDrivers.length}{" "}
                {pluralize("driver", unassignedOnlineDrivers.length)}
              </span>
            </div>
          </AccordionV2.Label>
          <AccordionV2.Content>
            <div
              className="flex flex-col bg-gray-100"
              aria-label="Unassigned Online Drivers"
            >
              {unassignedOnlineDrivers.map((assignee) => (
                <div
                  key={assignee.id}
                  className="flex items-start justify-between text-gray-900 sm:text-sm transition-colors duration-200 w-full hover:bg-gray-50 px-0 py-3 border-b border-gray-200 pr-2"
                >
                  <div className="flex flex-col items-start pl-4">
                    <span className="text-gray-700 font-normal text-xs lg:text-sm flex gap-2 items-center">
                      <div>
                        <Avatar
                          image={assignee.avatarUrl ?? undefined}
                          size={8}
                          dutyStatus={assignee.dutyStatus}
                        />
                      </div>
                      <span className="flex gap-2 self-start">
                        {assignee.name}
                        {isLocationPinVisible(assignee) && (
                          <Icon
                            name="locationCrossHairs"
                            className={clsx(
                              "w-3 cursor-pointer p-1 rounded-full hover:bg-gray-200",
                              focusedAssigneeId === assignee.driverProfileId
                                ? "bg-gray-200"
                                : "",
                            )}
                            onClick={() => {
                              useDispatchStore.setState({
                                focusedAssigneeId:
                                  focusedAssigneeId !== assignee.driverProfileId
                                    ? assignee.driverProfileId
                                    : null,
                              });
                            }}
                          />
                        )}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </AccordionV2.Content>
        </AccordionV2>
      )}
    </div>
  );
}
